import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Wallet, ChevronRight, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { navGroups } from "@/components/layout/navItems";
import BrandMark from "@/components/common/BrandMark";
import BuildInfo from "@/components/common/BuildInfo";

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || "OneX").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <aside className="hidden lg:flex flex-col fixed left-0 top-0 h-screen w-[280px] bg-[#0A0A0B] border-r border-[#1A1B1F] px-4 py-6 z-40">
      {/* Brand */}
      <div className="flex items-center gap-3 px-2 mb-2" data-testid="brand-block">
        <BrandMark size={48} glow />
        <div>
          <div className="text-[17px] font-semibold leading-none">
            <span className="text-white">OneX</span> <span className="onex-gold-text">Club</span>
          </div>
          <div className="text-[10px] tracking-[0.2em] text-zinc-500 mt-1.5 uppercase">Dubai · Assets</div>
        </div>
      </div>

      <nav className="flex-1 mt-4 overflow-y-auto onex-scroll pr-1">
        {navGroups.map((group) => (
          <div key={group.label}>
            <div className="onex-sidebar-label">{group.label}</div>
            {group.items.map(({ to, icon: Icon, name, testId }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testId}
                className={({ isActive }) => `onex-sidebar-item ${isActive ? "active" : ""}`}
              >
                <Icon size={18} strokeWidth={1.8} />
                <span>{name}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* AED balance card */}
      <button
        type="button"
        onClick={() => navigate("/benefits-ladder")}
        data-testid="sidebar-aed-balance-card"
        className="mt-4 w-full flex items-center justify-between gap-3 rounded-2xl border border-[#27272A] bg-[#15161A] hover:border-[#8CFF2E]/40 transition-all px-4 py-3"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#8CFF2E]/10 border border-[#8CFF2E]/30 flex items-center justify-center">
            <Wallet size={16} className="text-[#8CFF2E]" />
          </div>
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Co-Owner Credits</div>
            <div className="text-[15px] font-semibold text-white">AED {user?.aed_balance ?? 0}</div>
          </div>
        </div>
        <ChevronRight size={16} className="text-zinc-500" />
      </button>

      {/* Profile card */}
      <button
        type="button"
        onClick={() => navigate("/settings")}
        data-testid="sidebar-profile-card"
        className="mt-3 w-full flex items-center justify-between gap-3 rounded-2xl border border-[#27272A] bg-[#15161A] hover:border-[#8CFF2E]/30 transition-all px-3 py-2.5"
      >
        <div className="flex items-center gap-3">
          {user?.picture ? (
            <img src={user.picture} alt={user?.name} className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-[#8CFF2E]/15 text-[#8CFF2E] flex items-center justify-center font-semibold text-sm">{initials}</div>
          )}
          <div className="text-left">
            <div className="text-[14px] font-medium text-white leading-tight">{user?.name?.split(" ")[0] || "Member"}</div>
            <div className="text-[11px] text-zinc-500">{user?.tier || "Member"} Tier</div>
          </div>
        </div>
        <ChevronDown size={14} className="text-zinc-500" />
      </button>

      <button
        type="button"
        onClick={logout}
        data-testid="sidebar-logout-btn"
        className="mt-2 text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-[0.2em] py-1"
      >
        Sign out
      </button>
      <div className="mt-3 text-[10px] text-zinc-600">
        <BuildInfo>v1.0.0 · hold for build info</BuildInfo>
      </div>
    </aside>
  );
};

export default Sidebar;
